import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageOff, Search, Loader2, Tag } from 'lucide-react';
import { useRecipeStore } from '../store/recipeStore';
import type { Recipe } from '../types/recipe';

export function RecipeList() {
  const navigate = useNavigate();
  const { recipes, loading } = useRecipeStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (recipeId: string) => {
    setFailedImages(prev => new Set([...prev, recipeId]));
  };

  const filteredRecipes = useMemo(() => {
    if (!searchQuery.trim()) return recipes;
    
    const query = searchQuery.toLowerCase();
    return recipes.filter(recipe => 
      recipe.title.toLowerCase().includes(query) ||
      recipe.ingredients.some(ing => 
        ing.name.toLowerCase().includes(query) ||
        ing.base_ingredient.toLowerCase().includes(query)
      ) ||
      recipe.tags?.some(tag => 
        tag.toLowerCase().includes(query)
      )
    );
  }, [recipes, searchQuery]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-600">Loading recipes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search recipes by name, ingredient, or tag..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Search 
          size={20}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
      </div>

      {/* Results */}
      {filteredRecipes.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {searchQuery ? 'No recipes found matching your search' : 'No recipes yet. Import some recipes to get started!'}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRecipes.map((recipe) => (
            <div 
              key={recipe.id} 
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate(`/recipes/${recipe.id}`)}
            >
              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {recipe.image_url && !failedImages.has(recipe.id) ? (
                      <img
                        src={recipe.image_url}
                        alt={recipe.title}
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(recipe.id)}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <ImageOff size={20} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-900 line-clamp-1">{recipe.title}</h3>
                    <div className="mt-1 text-sm text-gray-600">
                      {recipe.ingredients.length} ingredients • {recipe.instructions.length} steps
                    </div>
                    {recipe.tags && recipe.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {recipe.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            <Tag size={12} />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}